import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TokenCard from '../components/TokenCard';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { X } from 'lucide-react';


export default function TokenSummary() {
    const [tokens, setTokens] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [notification, setNotification] = useState(null);
    const [wallet, setWallet] = useState({
        balance: 0,
        openPositionsValue: 0,
        totalPnL: 0
    });


    const showNotification = (type, title, message) => {
        setNotification({ type, title, message });
        setTimeout(() => setNotification(null), 5000);
    };

    const fetchTokens = async () => {
        try {
            setError(null);
            const response = await axios.get('https://solana-callers-bot-production.up.railway.app/api/monitoringTokens');
            setTokens(response.data);
            setLastUpdated(new Date());
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching tokens:', error);
            setError('Failed to fetch token data');
            setIsLoading(false);
        }
    };

    const fetchWallet = async () => {
        try {
            const response = await axios.get('https://solana-callers-bot-production.up.railway.app/api/wallet');
            setWallet(response.data);
        } catch (error) {
            console.error('Error fetching wallet:', error);
        }
    };

    useEffect(() => {
        fetchTokens();
        fetchWallet();

        const tokenPollInterval = setInterval(fetchTokens, 5000);
        const walletPollInterval = setInterval(fetchWallet, 5000);

        return () => {
            clearInterval(tokenPollInterval);
            clearInterval(walletPollInterval);
        };
    }, []);

    const formatLastUpdated = () => {
        if (!lastUpdated) return '';
        return lastUpdated.toLocaleTimeString();
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>
                    {error}
                    <button
                        onClick={fetchTokens}
                        style={styles.retryButton}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const handleCloseAllPositions = async () => {
        try {
            const response = await fetch('https://solana-callers-bot-production.up.railway.app/api/closePositions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();

            if (response.ok) {
                showNotification('success', 'Success', data.message);
            } else {
                showNotification('error', 'Error', data.error);
            }
        } catch (error) {
            showNotification('error', 'Error', 'Failed to close position');
        }
    };

    return (
        <div style={styles.container}>
            {notification && (
                <div style={{
                    position: 'fixed',
                    top: '16px',
                    right: '16px',
                    width: '384px',
                    zIndex: 50
                }}>
                    <Alert style={{
                        backgroundColor: notification.type === 'success' ? '#f0fdf4' : '#fef2f2',
                        borderColor: notification.type === 'success' ? '#bbf7d0' : '#fecaca',
                        marginBottom: '8px'
                    }}>
                        <AlertTitle style={{
                            color: notification.type === 'success' ? '#166534' : '#991b1b'
                        }}>
                            {notification.title}
                        </AlertTitle>
                        <AlertDescription style={{
                            color: notification.type === 'success' ? '#15803d' : '#b91c1c'
                        }}>
                            {notification.message}
                        </AlertDescription>
                        <button
                            onClick={() => setNotification(null)}
                            style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px'
                            }}
                        >
                            <X style={{ height: '16px', width: '16px' }} />
                        </button>
                    </Alert>
                </div>
            )}
            <h1 style={styles.header}>📊 Open positions live 📊</h1>

            {/* Last Updated Timestamp */}
            {lastUpdated && (
                <div style={styles.updateInfo}>
                    Last updated: {formatLastUpdated()}
                </div>
            )}

            <div onClick={handleCloseAllPositions}>
                <button style={styles.retryButton}>
                    Close all current opened positions
                </button></div>

            <div style={styles.walletInfo}>
                <div style={styles.walletRow}>
                    <span>Available Balance:</span>
                    <span style={styles.walletValue}>{formatCurrency(wallet.balance)}</span>
                </div>
                <div style={styles.walletRow}>
                    <span>Open Positions Value:</span>
                    <span style={styles.walletValue}>{formatCurrency(wallet.openPositionsValue)}</span>
                </div>
                <div style={styles.walletRow}>
                    <span>Total P&L:</span>
                    <span style={{
                        ...styles.walletValue,
                        color: wallet.totalPnL >= 0 ? '#15803d' : '#b91c1c'
                    }}>
                        {formatCurrency(wallet.totalPnL)}
                    </span>
                </div>
            </div>

            {/* Loading State */}
            {isLoading && !tokens.length ? (
                <div style={styles.loading}>
                    Loading token data...
                </div>
            ) : (
                /* Token Cards */
                <div style={styles.tokenGrid}>
                    {tokens.length > 0 ? (
                        tokens.map((token, index) => (
                            <TokenCard key={index} token={token} />
                        ))
                    ) : (
                        <div style={styles.noData}>
                            No tokens currently being monitored
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    walletInfo: {
        backgroundColor: '#f8fafc',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    walletRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #e2e8f0',
    },
    walletValue: {
        fontWeight: 'bold',
        fontFamily: 'monospace',
    },
    container: {
        padding: '50px',
    },
    header: {
        color: '#000000',
        textAlign: 'center',
        marginBottom: '20px',
    },
    updateInfo: {
        textAlign: 'center',
        color: '#666',
        marginBottom: '20px',
        fontSize: '0.9em',
    },
    loading: {
        textAlign: 'center',
        padding: '20px',
        color: '#666',
    },
    error: {
        color: 'red',
        padding: '20px',
        marginBottom: '20px',
        backgroundColor: '#ffe6e6',
        borderRadius: '5px',
        textAlign: 'center',
    },
    retryButton: {
        marginLeft: '10px',
        padding: '5px 10px',
        backgroundColor: '#ff4444',
        color: 'white',
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
    },
    tokenGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
        padding: '20px 0',
    },
    noData: {
        textAlign: 'center',
        padding: '40px',
        color: '#666',
        backgroundColor: '#f5f5f5',
        borderRadius: '5px',
        gridColumn: '1 / -1',
    },
};